import React, { useRef, useEffect } from 'react';
import { ButtonDropdown, Flashbar } from '@amzn/awsui-components-core';
import { createPortal } from 'react-dom';
import { ResolvedMatch } from '../../interfaces/interface';
import { MeetingService } from '../../services/MeetingService';
import { copyTextWithFeedback, ClipboardError, ClipboardResult } from '../../utils/clipboardUtils';
import { useFlashMessages } from '../../hooks/useFlashMessages';
import './CopyDropdown.scss';

// Menu item text constants - exported for testing
export const MENU_ITEMS = {
  ATTENDEES: {
    SECTION: 'Attendees',
    EMAILS: 'Copy Emails for Outlook',
    LIST: 'Copy Attendees List'
  },
  EXECUTIVE_ASSISTANTS: {
    SECTION: 'Executive Assistants',
    EMAILS: 'Copy Emails for Outlook',
    LIST: 'Copy EAs List'
  }
} as const;

interface CopyDropdownProps {
  resolvedAttendees: ResolvedMatch[];
  executiveAssistants: Array<{ name: string; email: string }>;
  onMultiBadgerClick?: () => void;
  onDownloadCsv?: () => void;
}

/**
 * Helper function to count the number of lines in formatted text
 * Used for accurate counting after deduplication in MeetingService
 */
const getCountFromFormattedText = (text: string): number => {
  return text.split('\n').length;
};

export const CopyDropdown: React.FC<CopyDropdownProps> = ({ resolvedAttendees, executiveAssistants, onMultiBadgerClick, onDownloadCsv }) => {
  const { flashMessages, addFlashMessage, removeFlashMessage, announceMessage } = useFlashMessages({});
  const buttonRef = useRef<HTMLDivElement>(null);
  const [flashPosition, setFlashPosition] = React.useState({ top: 0, left: 0 });

  // Update flash message position when messages change
  useEffect(() => {
    if (flashMessages.length > 0 && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setFlashPosition({
        top: rect.bottom + window.scrollY + 8,
        left: Math.max(0, rect.left + window.scrollX - 150) // Prevent going off-screen left
      });
    }
  }, [flashMessages.length]);

  // Handle copy attendees for Outlook
  const handleCopyAttendeesForOutlook = async () => {
    const formattedText = MeetingService.formatAttendeesForCopy(resolvedAttendees);

    if (!formattedText) {
      addFlashMessage('error', 'No valid attendees with email addresses found to copy.');
      return;
    }

    await copyTextWithFeedback(
      formattedText,
      (result: ClipboardResult) => {
        const attendeeData = MeetingService.buildAttendeeData(resolvedAttendees);
        const attendeeCount = [...attendeeData.requiredWithNames, ...attendeeData.optionalWithNames].length;
        addFlashMessage('success', `Copied ${attendeeCount} attendee${attendeeCount !== 1 ? 's' : ''} to clipboard for Outlook.`);
      },
      (error: ClipboardError) => {
        addFlashMessage('error', 'Failed to copy to clipboard. Please try again.');
      }
    );
  };

  // Handle copy executive assistants for Outlook
  const handleCopyExecutiveAssistantsForOutlook = async () => {
    const formattedText = MeetingService.formatExecutiveAssistantsForCopy(resolvedAttendees);

    if (!formattedText) {
      addFlashMessage('error', 'No valid executive assistants with email addresses found to copy.');
      return;
    }

    await copyTextWithFeedback(
      formattedText,
      (result: ClipboardResult) => {
        const executiveAssistants = MeetingService.collectExecutiveAssistants(resolvedAttendees);
        const assistantCount = executiveAssistants.length;
        addFlashMessage('success', `Copied ${assistantCount} executive assistant${assistantCount !== 1 ? 's' : ''} to clipboard for Outlook.`);
      },
      (error: ClipboardError) => {
        addFlashMessage('error', 'Failed to copy to clipboard. Please try again.');
      }
    );
  };

  // Handle copy employee details
  const handleCopyEmployeeDetails = async () => {
    const formattedText = MeetingService.formatEmployeeDetailsForCopy(resolvedAttendees);

    if (!formattedText) {
      addFlashMessage('error', 'No employee details found to copy.');
      return;
    }

    await copyTextWithFeedback(
      formattedText,
      (result: ClipboardResult) => {
        const employeeCount = getCountFromFormattedText(formattedText);
        addFlashMessage('success', `Copied details for ${employeeCount} employee${employeeCount !== 1 ? 's' : ''} to clipboard.`);
      },
      (error: ClipboardError) => {
        addFlashMessage('error', 'Failed to copy to clipboard. Please try again.');
      }
    );
  };

  // Handle copy executive assistant details
  const handleCopyExecutiveAssistantDetails = async () => {
    const formattedText = MeetingService.formatExecutiveAssistantDetailsForCopy(resolvedAttendees);

    if (!formattedText) {
      addFlashMessage('error', 'No executive assistant details found to copy.');
      return;
    }

    await copyTextWithFeedback(
      formattedText,
      (result: ClipboardResult) => {
        const eaCount = getCountFromFormattedText(formattedText);
        addFlashMessage('success', `Copied details for ${eaCount} executive assistant${eaCount !== 1 ? 's' : ''} to clipboard.`);
      },
      (error: ClipboardError) => {
        addFlashMessage('error', 'Failed to copy to clipboard. Please try again.');
      }
    );
  };

  const hasValidAttendees = MeetingService.formatAttendeesForCopy(resolvedAttendees).length > 0;

  const hasValidExecutiveAssistants = MeetingService.formatExecutiveAssistantsForCopy(resolvedAttendees).length > 0;

  // Build dropdown items - flat structure with all 4 actions
  const dropdownItems = [
    {
      id: 'view-badger',
      text: 'View Hierarchy in Multi-Badger',
      iconName: 'external' as const,
      external: true,
      externalIconAriaLabel: '(opens Attendee Hierarchy in Badger in new tab)',
      disabled: !onMultiBadgerClick
    },
    {
      id: 'download-csv',
      text: 'Download as CSV',
      iconName: 'download' as const,
      disabled: !onDownloadCsv
    },
    {
      id: 'copy-attendee-emails',
      text: 'Copy Attendee Email Addresses',
      iconName: 'copy' as const,
      disabled: !hasValidAttendees
    },
    {
      id: 'copy-ea-emails',
      text: 'Copy EA Email Addresses',
      iconName: 'copy' as const,
      disabled: !hasValidExecutiveAssistants
    }
  ];

  return (
    <>
      <div ref={buttonRef} className="copy-dropdown-wrapper">
        {/* Attendee Hierarchy Dropdown */}
        <ButtonDropdown
          items={dropdownItems}
          variant="normal"
          disabled={resolvedAttendees.length === 0}
          onItemClick={({ detail }) => {
            if (detail.id === 'view-badger') {
              onMultiBadgerClick?.();
            } else if (detail.id === 'download-csv') {
              onDownloadCsv?.();
            } else if (detail.id === 'copy-attendee-emails') {
              handleCopyAttendeesForOutlook();
            } else if (detail.id === 'copy-ea-emails') {
              handleCopyExecutiveAssistantsForOutlook();
            }
          }}
          ariaLabel="Attendee Hierarchy options"
        >
          Attendee Hierarchy
        </ButtonDropdown>

        {/* ARIA live region for screen reader announcements */}
        <div aria-live="polite" aria-atomic="true" className="copy-dropdown-aria-live">
          {announceMessage}
        </div>
      </div>

      {/* Flash Messages - Portal-based positioning for better control */}
      {flashMessages.length > 0 &&
        createPortal(
          <div
            className="copy-dropdown-flashbar-container"
            style={{
              top: `${flashPosition.top}px`,
              left: `${flashPosition.left}px`
            }}
          >
            <Flashbar
              items={flashMessages.map((msg) => ({
                type: msg.type,
                content: msg.content,
                dismissible: true,
                onDismiss: () => removeFlashMessage(msg.id),
                id: msg.id
              }))}
            />
          </div>,
          document.body
        )}
    </>
  );
};
